import React, { useState, useEffect } from 'react';
import {
  DndContext, closestCenter, DragOverlay, useSensor, useSensors, PointerSensor, useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Eye, EyeOff, ArrowLeft, ArrowRight } from 'lucide-react';
import client from '../../api/client';

const ALL_WIDGETS = [
  { id: 'vitals', label: 'Vitals', icon: '💊', desc: 'Case age, task counts, financials' },
  { id: 'summary', label: 'Summary', icon: '📋', desc: 'Key record fields at a glance' },
  { id: 'notes', label: 'Notes', icon: '📝', desc: 'Notes with quick-add' },
  { id: 'activity', label: 'Activity', icon: '⚡', desc: 'Recent record history' },
  { id: 'tasks', label: 'Tasks', icon: '✅', desc: 'Task list with progress' },
  { id: 'documents', label: 'Documents', icon: '📎', desc: 'Uploaded files' },
  { id: 'calendar', label: 'Calendar', icon: '📅', desc: 'Mini calendar with deadlines' },
  { id: 'team', label: 'Team', icon: '👥', desc: 'Assigned users and team' },
  { id: 'linked', label: 'Linked Records', icon: '🔗', desc: 'Related records from other types' },
  { id: 'portal_messages', label: 'Portal Messages', icon: '💬', desc: 'Client portal messaging' },
];

const DEFAULT_CONFIG = {
  left: ['vitals', 'summary', 'notes', 'activity'],
  right: ['tasks', 'documents', 'calendar', 'team', 'linked', 'portal_messages'],
  hidden: [],
};

function SortableWidget({ id, widget, column, onMove, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column + '-' + id,
    data: { widget: id, column },
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center gap-2 p-2.5 rounded-lg border bg-white ${isDragging ? 'border-brand-300 shadow-lg' : 'border-gray-200'} group`}>
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
        <GripVertical className="h-4 w-4" />
      </div>
      <span className="text-sm">{widget.icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">{widget.label}</p>
        <p className="text-[10px] text-gray-400 truncate">{widget.desc}</p>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {column !== 'hidden' && (
          <button onClick={() => onMove(id, column, column === 'left' ? 'right' : 'left')}
            title={column === 'left' ? 'Move to right' : 'Move to left'}
            className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            {column === 'left' ? <ArrowRight className="h-3 w-3" /> : <ArrowLeft className="h-3 w-3" />}
          </button>
        )}
        <button onClick={() => onToggle(id, column)}
          title={column === 'hidden' ? 'Show' : 'Hide'}
          className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
          {column === 'hidden' ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
        </button>
      </div>
    </div>
  );
}

function Column({ title, subtitle, columnId, items, onMove, onToggle, allWidgets }) {
  const sortableIds = items.map(id => columnId + '-' + id);
  const { setNodeRef, isOver } = useDroppable({ id: 'droppable-' + columnId, data: { column: columnId } });
  return (
    <div className="flex-1 min-w-0">
      <div className="mb-2">
        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">{title}</h4>
        {subtitle && <p className="text-[10px] text-gray-400">{subtitle}</p>}
      </div>
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        <div ref={setNodeRef} className={`space-y-1.5 min-h-[100px] p-2 rounded-lg border-2 border-dashed transition-colors ${
          isOver ? 'border-brand-400 bg-brand-50' : columnId === 'hidden' ? 'border-gray-200 bg-gray-50' : 'border-gray-300 bg-white'
        }`}>
          {items.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-6">
              {columnId === 'hidden' ? 'No hidden widgets' : 'Drop widgets here'}
            </p>
          )}
          {items.map(id => {
            const w = allWidgets.find(w => w.id === id);
            if (!w) return null;
            return <SortableWidget key={id} id={id} widget={w} column={columnId} onMove={onMove} onToggle={onToggle} />;
          })}
        </div>
      </SortableContext>
    </div>
  );
}

export default function WidgetConfigurator({ config, onChange }) {
  const [diaryWidgets, setDiaryWidgets] = useState([]);
  useEffect(() => {
    client.get('/diary/types').then(({ data }) => {
      const custom = (data.diary_types || []).filter(dt => !dt.is_system && dt.is_active !== false && (dt.tracked_fields || []).length > 0);
      setDiaryWidgets(custom.map(dt => ({
        id: 'diary:' + dt.id,
        label: dt.name,
        icon: dt.icon || '📖',
        desc: 'Diary: ' + dt.name + ' (' + (dt.tracked_fields || []).length + ' tracked fields)'
      })));
    }).catch(() => {});
  }, []);
  const allWidgets = [...ALL_WIDGETS, ...diaryWidgets];

  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const cfg = { left: [...(config?.left || DEFAULT_CONFIG.left)], right: [...(config?.right || DEFAULT_CONFIG.right)], hidden: [...(config?.hidden || [])] };
  allWidgets.forEach(w => {
    if (!cfg.left.includes(w.id) && !cfg.right.includes(w.id) && !cfg.hidden.includes(w.id)) {
      cfg.hidden.push(w.id);
    }
  });

  const handleMove = (widgetId, fromCol, toCol) => {
    const next = { left: [...cfg.left], right: [...cfg.right], hidden: [...cfg.hidden] };
    next[fromCol] = next[fromCol].filter(id => id !== widgetId);
    next[toCol] = [...next[toCol], widgetId];
    onChange(next);
  };

  const handleToggle = (widgetId, fromCol) => {
    handleMove(widgetId, fromCol, fromCol === 'hidden' ? 'left' : 'hidden');
  };

  const parseId = (compositeId) => {
    const parts = compositeId.split('-');
    return { column: parts[0], widget: parts.slice(1).join('-') };
  };

  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const from = parseId(active.id);
    const next = { left: [...cfg.left], right: [...cfg.right], hidden: [...cfg.hidden] };

    // Check if dropped on a droppable container (empty column)
    const overId = String(over.id);
    if (overId.startsWith('droppable-')) {
      const toCol = overId.replace('droppable-', '');
      if (from.column !== toCol) {
        next[from.column] = next[from.column].filter(id => id !== from.widget);
        next[toCol].push(from.widget);
      }
      onChange(next);
      return;
    }

    const to = parseId(overId);
    if (from.column === to.column) {
      const oldIdx = next[from.column].indexOf(from.widget);
      const newIdx = next[to.column].indexOf(to.widget);
      if (oldIdx !== -1 && newIdx !== -1) next[from.column] = arrayMove(next[from.column], oldIdx, newIdx);
    } else {
      next[from.column] = next[from.column].filter(id => id !== from.widget);
      const targetIdx = next[to.column].indexOf(to.widget);
      if (targetIdx !== -1) next[to.column].splice(targetIdx, 0, from.widget);
      else next[to.column].push(from.widget);
    }
    onChange(next);
  };

  const activeWidget = activeId ? allWidgets.find(w => w.id === parseId(activeId).widget) : null;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={(e) => setActiveId(e.active.id)} onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-3 gap-4">
        <Column title="Left Panel" subtitle="Left side of form" columnId="left" items={cfg.left} onMove={handleMove} onToggle={handleToggle} allWidgets={allWidgets} />
        <Column title="Right Panel" subtitle="Right side of form" columnId="right" items={cfg.right} onMove={handleMove} onToggle={handleToggle} allWidgets={allWidgets} />
        <Column title="Hidden" subtitle="Not shown" columnId="hidden" items={cfg.hidden} onMove={handleMove} onToggle={handleToggle} allWidgets={allWidgets} />
      </div>
      <DragOverlay>
        {activeWidget && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg border border-brand-300 bg-white shadow-xl">
            <GripVertical className="h-4 w-4 text-gray-400" />
            <span className="text-sm">{activeWidget.icon}</span>
            <p className="text-sm font-medium text-gray-900">{activeWidget.label}</p>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

export { DEFAULT_CONFIG, ALL_WIDGETS };
